import { Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React, { useMemo, useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { get } from 'lodash';
import * as Yup from 'yup';
import { FormikCheckbox } from '../../../common/formik-material-ui';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import OnboardingSkeleton from './OnboardingSkeleton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    formControl: {
      margin: theme.spacing(3),
    },
  })
);

const BuildTeams = ({
  loading,
  onboardingClasses,
  handleStep,
  handleBack,
  mutation,
  userAtom,
  enqueueSnackbarWithStyles,
  activeStep,
  owWizardDataParsed,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const classes = useStyles();

  const hasAtLeastOneDepartment = (obj) =>
    (obj?.accountManagement ||
      obj?.customerService ||
      obj?.finance ||
      obj?.sales ||
      obj?.other) &&
    ((obj?.other === true && (obj?.otherDepartments?.length ?? 0) > 0) ||
      (obj?.other === false && obj?.otherDepartments?.length === 0));

  const validationSchema = useMemo(
    () =>
      Yup.object()
        .shape({
          accountManagement: Yup.bool(),
          customerService: Yup.bool(),
          finance: Yup.bool(),
          sales: Yup.bool(),
          other: Yup.bool(),
          otherDepartments: Yup.array().when('other', {
            is: (other) => other === true,
            then: Yup.array().required('required'),
          }),
        })
        .test('build_teams_validation_error', 'One team is required', function (
          obj
        ) {
          if (hasAtLeastOneDepartment(obj)) {
            return true;
          } else {
            return this.createError({
              message: 'One team is required',
              path: 'otherDepartments',
            });
          }
        }),
    []
  );

  const handleNext = (formik) => {
    if (formik.isValid) {
      formik.submitForm();
    }
  };

  const buildTeamInitialValues = useMemo(() => {
    setSelectedOptions(owWizardDataParsed?.buildTeams?.otherDepartments ?? []);
    return {
      sales: owWizardDataParsed?.buildTeams?.sales ?? false,
      accountManagement:
        owWizardDataParsed?.buildTeams?.accountManagement ?? false,
      customerService: owWizardDataParsed?.buildTeams?.customerService ?? false,
      finance: owWizardDataParsed?.buildTeams?.finance ?? false,
      other: owWizardDataParsed?.buildTeams?.other ?? false,
      otherDepartments: owWizardDataParsed?.buildTeams?.otherDepartments ?? [],
    };
  }, [owWizardDataParsed]);

  return loading ? (
    <OnboardingSkeleton />
  ) : (
    <div style={{ padding: '24px' }}>
      <Typography
        className={onboardingClasses.onboardingSubtitle}
        variant={'h6'}
      >
        ACCOUNT - CREATING YOUR TEAMS
      </Typography>
      <div style={{ fontWeight: 400, marginTop: 20 }}>
        Which teams do your customers interact with on the phone?
      </div>
      <Formik
        enableReinitialize={true}
        initialValues={buildTeamInitialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, _actions) => {
          const updateOnboardingWizard = () =>
            mutation.mutateAsync({
              endpoint: endpointEnum.UPDATE_OW_WIZARD,
              domain: process.env.VITE_APP_CDK_DOMAIN,
              method: 'put',
              stage: 'itn',
              payload: {
                apiKey: userAtom?.apikey,
                businessId: userAtom?.businessid,
                owWizardData: JSON.stringify({
                  ...owWizardDataParsed,
                  activeStep: 2,
                  buildTeams: values ?? [],
                }),
              },
            });

          try {
            const [onboardingWizard] = await Promise.allSettled<any>([
              updateOnboardingWizard(),
            ]);

            const isSuccessful = (result) =>
              result.status === 'fulfilled' ||
              (result.status === 'rejected' &&
                result.reason.response?.status === 304);

            if (isSuccessful(onboardingWizard)) {
              enqueueSnackbarWithStyles({
                message: 'Successfully Updated Onboarding Wizard',
                alertProps: { severity: 'success' },
              });
              handleStep(2);
            } else {
              enqueueSnackbarWithStyles({
                message: 'Encountered an error updating Onboarding Wizard',
                alertProps: { severity: 'error' },
              });
            }
          } catch (err) {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error updating Onboarding Wizard',
              alertProps: { severity: 'error' },
            });
          }
        }}
      >
        {(formik) => (
          <div className={classes.root}>
            <FormControl
              component="fieldset"
              className={classes.formControl}
              style={{ marginTop: 10, marginBottom: 0 }}
            >
              {Object.keys(formik?.values).map((team, index) => {
                const result = team.replace(/([A-Z])/g, ' $1');
                const finalResult =
                  result.charAt(0).toUpperCase() + result.slice(1);
                return team !== 'otherDepartments' ? (
                  <div
                    key={team}
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <FormControlLabel
                      key={index}
                      control={
                        <FormikCheckbox
                          color="primary"
                          formik={formik}
                          name={`${team}`}
                          style={{ paddingTop: 5, paddingBottom: 5 }}
                          onClick={(e) => {
                            if (team === 'other') {
                              setSelectedOptions([]);
                              formik.setFieldValue('otherDepartments', []);
                            }
                          }}
                        />
                      }
                      label={team !== 'other' ? finalResult : 'Other Teams'}
                    />
                  </div>
                ) : (
                  <div
                    key={team}
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    {formik.values.other && (
                      <Autocomplete
                        multiple
                        freeSolo
                        id="onboarding-build-teams-input"
                        value={selectedOptions}
                        options={selectedOptions ?? []}
                        // disableCloseOnSelect
                        getOptionLabel={(option) => option}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <React.Fragment>
                              {selected ? <strong>{option}</strong> : option}
                            </React.Fragment>
                          </li>
                        )}
                        style={{ width: 500 }}
                        onChange={(
                          _event,
                          selectedOptions,
                          reason,
                          _details
                        ) => {
                          if (
                            reason === 'selectOption' ||
                            reason === 'createOption' ||
                            reason === 'removeOption'
                          ) {
                            setSelectedOptions(selectedOptions);
                            formik.setFieldValue(
                              'otherDepartments',
                              selectedOptions
                            );
                          } else if (reason === 'clear') {
                            setSelectedOptions([]);
                            formik.setFieldValue('otherDepartments', []);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            required={formik.values.other}
                            {...params}
                            size="small"
                            name={'otherDepartments'}
                            variant="outlined"
                            label="Other Teams"
                            placeholder="Departments"
                            error={
                              formik &&
                              !!get(formik.touched, 'otherDepartments') &&
                              !!get(formik.errors, 'otherDepartments')
                            }
                            helperText="Type the team name and press enter to add
                              "
                          />
                        )}
                      />
                    )}
                  </div>
                );
              })}
            </FormControl>
            <div style={{ padding: '24px 0px 0px' }}>
              <Button
                onClick={() => handleBack(activeStep - 1)}
                className={onboardingClasses.button}
              >
                Back
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNext(formik)}
                className={onboardingClasses.button}
                disabled={
                  !formik?.isValid ||
                  (!formik?.dirty && !hasAtLeastOneDepartment(formik?.values))
                }
                endIcon={<ArrowForwardIcon />}
              >
                Continue
              </Button>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default BuildTeams;
